import React from 'react';

import { Colors } from '@cfa/react-components/dist/cjs/internal/types';
import { ButtonProps } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import { PathwayModal } from '@/components/popups/PathwayModal';

interface ConfirmationModalBaseProps {
  bodyText: string;
  bodyTextColor?: string;
  children?: React.ReactNode;
  className?: string;
  headerText: string;
  isDisabled?: boolean;
  isError?: boolean;
  isLoading?: boolean;
  primaryButtonColor?: Colors;
  primaryButtonHandler?: () => void;
  primaryButtonText: string;
  primaryButtonVariant?: ButtonProps['variant'];
  secondaryButtonHandler?: () => void;
  secondaryButtonColor?: Colors;
  secondaryButtonText?: string;
}

interface ConfirmationModalStandaloneProps extends ConfirmationModalBaseProps {
  isStep?: false;
  isOpen: boolean;
  onClose?: () => void;
  overlayProps?: Record<string, unknown>;
  showCloseButton?: boolean;
  stepKey?: string;
}

interface ConfirmationModalStepProps extends ConfirmationModalBaseProps {
  /** Renders as a slot fragment (Header/Body/Footer) for use inside a parent PathwayModal step. */
  isStep: true;
}

type ConfirmationModalProps =
  | ConfirmationModalStandaloneProps
  | ConfirmationModalStepProps;

const ConfirmationModalContent: React.FC<ConfirmationModalBaseProps> = ({
  bodyText,
  bodyTextColor,
  children,
  className,
  headerText,
  isDisabled,
  isError,
  isLoading,
  primaryButtonColor = 'secondary',
  primaryButtonHandler,
  primaryButtonText,
  primaryButtonVariant = 'filled',
  secondaryButtonColor = 'secondary',
  secondaryButtonHandler,
  secondaryButtonText,
}) => (
  <>
    <PathwayModal.Header>
      <PathwayModal.Title>{headerText}</PathwayModal.Title>
    </PathwayModal.Header>
    <PathwayModal.Body noMinHeight>
      <div
        className={cn(
          className,
          bodyTextColor ? 'text-feedback-success-base!' : 'text-inherit!',
        )}
        dangerouslySetInnerHTML={{ __html: bodyText }}
      />
      {children && <div className="mx-auto">{children}</div>}
    </PathwayModal.Body>
    <PathwayModal.Footer>
      {secondaryButtonHandler && secondaryButtonText && (
        <PathwayModal.SecondaryButton
          color={secondaryButtonColor}
          onPress={secondaryButtonHandler}
          testId="ConfirmationPopUpSecondaryButton"
        >
          {secondaryButtonText}
        </PathwayModal.SecondaryButton>
      )}
      <PathwayModal.PrimaryButton
        color={primaryButtonColor}
        isDisabled={isDisabled}
        isPending={isLoading}
        onPress={primaryButtonHandler}
        testId="ConfirmationPopUpPrimaryButton"
        variant={isError ? 'destructive' : primaryButtonVariant}
      >
        {primaryButtonText}
      </PathwayModal.PrimaryButton>
    </PathwayModal.Footer>
  </>
);

const ConfirmationModal: React.FC<ConfirmationModalProps> = props => {
  if (props.isStep) {
    return <ConfirmationModalContent {...props} />;
  }

  const {
    className,
    isOpen,
    onClose,
    overlayProps,
    showCloseButton = true,
    stepKey,
    ...rest
  } = props;

  return (
    <PathwayModal
      className={cn(className, '2xl:max-w-[610px]!')}
      isOpen={isOpen}
      onClose={onClose ?? (() => {})}
      overlayClassName={overlayProps?.className as string | undefined}
      overlayStyle={overlayProps?.style as React.CSSProperties | undefined}
      showCloseButton={showCloseButton}
      stepKey={stepKey}
    >
      <ConfirmationModalContent {...rest} />
    </PathwayModal>
  );
};

export default ConfirmationModal;
